import React, { useState } from 'react'
import { Field, FieldArray } from 'redux-form'

const DIAGNOSIS_CODES = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L']
const MAX_DIAGNOSIS_CODES = 12
const MAX_PROCEDURE_ROWS = 15

const SYSTEMIC_CONDITIONS = [
  ['high_risk_for_prediabetes', 'High Risk for Prediabetes'],
  ['diabetes', 'Diabetes'],
  ['hypertension', 'Hypertension'],
  ['high_cholesterol', 'High Cholesterol'],
]

const OCULAR_CONDITIONS = [
  ['diabetic_retinopathy', 'Diabetic Retinopathy'],
  ['glaucoma', 'Glaucoma'],
  ['amd', 'AMD'],
  ['none', 'None of these 7 conditions'],
]

const inputClasses = 'form-control eyefinity-service-input'

const styles = {
  section: {
    border: '1px solid #ddd',
    background: '#fff',
  },
  header: {
    background: '#297ebb',
    color: '#fff',
    padding: '12px 18px',
    fontSize: 18,
    fontWeight: 400,
  },
  body: {
    padding: '24px 14px 28px',
  },
  label: {
    color: '#344052',
    fontSize: 12,
    fontWeight: 400,
  },
  diagnosisBadge: {
    background: '#297ebb',
    color: '#fff',
    minWidth: 36,
    height: 32,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 600,
  },
  procedureRowAlt: {
    background: '#eaf6fd',
  },
}

const renderTextInput = ({ input, placeholder, disabled, readOnly, type = 'text' }) => (
  <input
    {...input}
    type={type}
    readOnly={readOnly}
    disabled={disabled}
    placeholder={placeholder}
    className={inputClasses}
  />
)

const renderSelect = ({ input, children }) => (
  <select {...input} className={inputClasses}>
    {children}
  </select>
)

const renderCheckbox = ({ input }) => (
  <input
    {...input}
    type="checkbox"
    checked={!!input.value}
    className="eyefinity-service-checkbox"
  />
)

const DiagnosisField = ({ code, index }) => (
  <div className="col-md-3 col-sm-6 mb-3">
    <div className="d-flex">
      <span style={styles.diagnosisBadge}>{code}</span>
      <Field
        name={`service.diagnosis[${index}]`}
        component={renderTextInput}
      />
    </div>
  </div>
)

const KnownConditionRow = ({ label, conditions }) => (
  <div className="d-flex align-items-center mb-3 flex-wrap">
    <span className="mr-3" style={styles.label}>{label}</span>
    {conditions.map(([name, conditionLabel]) => (
      <label key={name} className="d-flex align-items-center mr-6 mb-0" style={styles.label}>
        <Field name={`service.${name}`} component={renderCheckbox} />
        <span className="ml-2">{conditionLabel}</span>
      </label>
    ))}
  </div>
)

const ProcedureInput = ({ name, placeholder, disabled, readOnly, type }) => (
  <Field
    name={name}
    component={renderTextInput}
    disabled={disabled}
    placeholder={placeholder}
    readOnly={readOnly}
    type={type}
  />
)

const renderProcedureRows = ({ fields }) => {
  const visibleRows = Math.min(Math.max(fields.length, 6), MAX_PROCEDURE_ROWS)
  const rowIndexes = Array.from({ length: visibleRows }, (_, index) => index)

  const addProcedure = () => {
    const currentLength = fields.getAll()?.length || 0

    const remainingRows = MAX_PROCEDURE_ROWS - currentLength

    if (remainingRows <= 0) return

    const rowsToAdd = Math.min(3, remainingRows)

    Array.from({ length: rowsToAdd }).forEach(() => {
      fields.push({})
    })

  }

  return (
    <>
      <div className="table-responsive">
        <table className="table mb-0 eyefinity-service-table">
          <thead>
            <tr>
              <th>24a.<br />From</th>
              <th>To</th>
              <th>24b.<br />POS</th>
              <th>24c.<br />EMG</th>
              <th>24d.<br />PROC</th>
              <th>Mods</th>
              <th>24e.<br />Diagnosis</th>
              <th>24f.<br />Charges</th>
              <th>24g.<br />Units</th>
              <th>24h.<br />EPSDT</th>
            </tr>
          </thead>
          <tbody>
            {rowIndexes.map(index => {
              const row = `${fields.name}[${index}]`

              return (
                <tr key={index} style={index % 2 ? styles.procedureRowAlt : undefined}>
                  <td><ProcedureInput name={`${row}.from`} placeholder="06/10/2026" /></td>
                  <td><ProcedureInput name={`${row}.to`} placeholder="06/10/2026" /></td>
                  <td><ProcedureInput name={`${row}.pos`} placeholder="11" disabled /></td>
                  <td className="text-center"><Field name={`${row}.emg`} component={renderCheckbox} /></td>
                  <td><ProcedureInput name={`${row}.proc`} /></td>
                  <td><ProcedureInput name={`${row}.modifier`} /></td>
                  <td>
                    <div className="d-flex">
                      {['A', 'B', 'C', 'D'].map((code, diagnosisIndex) => (
                        <ProcedureInput
                          key={code}
                          name={`${row}.diagnosis_pointers[${diagnosisIndex}]`}
                          placeholder={diagnosisIndex === 0 && index === 0 ? 'A' : ''}
                        />
                      ))}
                    </div>
                  </td>
                  <td><ProcedureInput name={`${row}.charges`} placeholder="$" /></td>
                  <td><ProcedureInput name={`${row}.unit_count`} /></td>
                  <td className="text-center"><Field name={`${row}.epsdt`} component={renderCheckbox} /></td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <div className="d-flex justify-content-end mt-5">
        <button
          type="button"
          className="btn btn-primary"
          disabled={visibleRows >= MAX_PROCEDURE_ROWS}
          onClick={addProcedure}
        >
          Add Procedures
        </button>
      </div>
    </>
  )
}

export default function EyefinityServiceSection() {
  const [diagnosisCount, setDiagnosisCount] = useState(8)
  const visibleDiagnosisCodes = DIAGNOSIS_CODES.slice(0, diagnosisCount)

  return (
    <div className="mt-5" style={styles.section}>
      <style>
        {`
          .eyefinity-service-input {
            height: 32px;
            border: 1px dashed #555;
            border-radius: 2px;
            background-color: #fff;
            color: #344052;
            font-size: 12px;
            padding: 4px 10px;
          }
          .eyefinity-service-input:focus {
            box-shadow: none;
            border-color: #333;
          }
          .eyefinity-service-checkbox {
            width: 14px;
            height: 14px;
            margin: 0;
            appearance: none;
            border: 1px dashed #777;
            border-radius: 4px;
            background: #fff;
          }
          .eyefinity-service-checkbox:checked {
            appearance: auto;
          }
          .eyefinity-service-table th {
            border: 0;
            color: #344052;
            font-size: 12px;
            font-weight: 400;
            line-height: 1.35;
            padding: 8px 8px 10px;
            white-space: nowrap;
          }
          .eyefinity-service-table td {
            border: 0;
            padding: 14px 8px;
            vertical-align: middle;
          }
          .eyefinity-service-table td:nth-child(7) .eyefinity-service-input {
            min-width: 42px;
            padding-left: 8px;
            padding-right: 8px;
          }
          .eyefinity-service-table td:nth-child(8) .eyefinity-service-input {
            min-width: 135px;
          }
        `}
      </style>

      <div style={styles.header}>Services</div>

      <div style={styles.body}>
        <div className="row mb-5">
          <div className="col-md-3 mb-4">
            <label style={styles.label}>PCP Communication Completed/Planned</label>
            <Field name="service.pcp_communication_completed_planned" component={renderSelect}>
              <option value="" />
              <option value="Yes">Yes</option>
              <option value="No - Reason on File">No - Reason on File</option>
            </Field>
            <div className="text-right mt-2">
              <button type="button" className="btn btn-link p-0" style={styles.label}>View PCP Form</button>
            </div>
          </div>

          <div className="col-md-9">
            <div className="mb-3" style={styles.label}>Known Conditions</div>
            <KnownConditionRow label="Systemic Conditions:" conditions={SYSTEMIC_CONDITIONS} />
            <KnownConditionRow label="Ocular Conditions:" conditions={OCULAR_CONDITIONS} />
          </div>
        </div>

        <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
          <label className="mb-2" style={styles.label}>
            <span className="text-danger">*</span> 21. Diagnosis or Nature of Illness or Injury (Relate items to diagnosis box 24e.)
          </label>
          <button type="button" className="btn btn-link p-0 mb-2">Show HCPCS/CPT Descriptions</button>
        </div>

        <div className="row mb-4">
          {visibleDiagnosisCodes.map((code, index) => <DiagnosisField key={code} code={code} index={index} />)}
        </div>

        {diagnosisCount < MAX_DIAGNOSIS_CODES && (
          <div className="d-flex justify-content-end mb-5">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setDiagnosisCount(count => Math.min(count + 4, MAX_DIAGNOSIS_CODES))}
            >
              Add Diagnosis Rows
            </button>
          </div>
        )}

        <FieldArray name="service.service_lines" component={renderProcedureRows} />

        <div className="row justify-content-end align-items-center mt-5">
          <div className="col-md-3 d-flex align-items-center mb-3">
            <label className="mr-2 mb-0 font-weight-bold" style={styles.label}>28. Total</label>
            <Field name="service.total" component={renderTextInput} placeholder="$0.00" readOnly />
          </div>
          <div className="col-md-3 d-flex align-items-center mb-3">
            <label className="mr-2 mb-0 font-weight-bold" style={styles.label}>
              <span className="text-danger">*</span> 29. Paid
            </label>
            <Field name="service.patient_paid" component={renderTextInput} placeholder="$" />
          </div>
          <div className="col-md-3 d-flex align-items-center mb-3">
            <label className="mr-2 mb-0 font-weight-bold" style={styles.label}>Due</label>
            <Field name="service.due" component={renderTextInput} placeholder="$0.00" readOnly />
          </div>
        </div>
      </div>
    </div>
  )
}
